import { useEffect, useRef, useState } from 'preact/hooks';

import { cx, type IconId } from '@amumin/ui-core';

import styles from './Tabs.module.css';

export interface TabItem<T extends string = string> {
  value: T;
  label: string;
  /**
   * Wordt niet meer getekend (WP1, docs/ux/ontwerpsysteem.md: tabs zijn tekst met een streep). Blijft
   * in het type, zodat bestaande schermen zonder wijziging werken; WP2 tot en met WP6 halen het weg.
   */
  icon?: IconId;
  /** Klein getal rechts (bv. open goedkeuringen). */
  count?: number;
}

export interface TabsProps<T extends string = string> {
  /** Prefix voor id's van tabs en panelen (`${idPrefix}-tab-x` / `${idPrefix}-panel-x`). */
  idPrefix: string;
  items: ReadonlyArray<TabItem<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Toegankelijke naam van de tablijst. */
  label: string;
}

/** Id-attributen voor het paneel dat bij een tab hoort. */
export function tabPanelProps(idPrefix: string, value: string) {
  return {
    id: `${idPrefix}-panel-${value}`,
    role: 'tabpanel' as const,
    'aria-labelledby': `${idPrefix}-tab-${value}`,
    tabIndex: 0,
  };
}

/**
 * Tabs met onderlijn (paginaniveau): onderdelen van een pagina, alleen tekst. Een weergave binnen
 * één onderdeel (Dag, Week) is een SegmentedControl; een filter is een Chip. Pijltjestoetsen wisselen, zoals WAI-ARIA voorschrijft. Op een
 * smal scherm scrollt de rij: de actieve tab schuift altijd helemaal in beeld en een zachte rand
 * rechts laat zien dat er meer is (polish w4 ronde 2, 390 px: nooit "W…" of "Klusse…").
 */
export function Tabs<T extends string>({ idPrefix, items, value, onChange, label }: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState(false);
  const updateFade = () => {
    const el = listRef.current;
    if (!el) return;
    const next = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    if (next !== fade) setFade(next);
  };
  useEffect(() => {
    const el = listRef.current;
    const tab = el?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (el && tab) {
      const left = tab.offsetLeft;
      const right = left + tab.offsetWidth;
      if (left < el.scrollLeft) el.scrollLeft = Math.max(0, left - 16);
      else if (right > el.scrollLeft + el.clientWidth) el.scrollLeft = right - el.clientWidth + 32;
    }
    updateFade();
  }, [value, items.length]);
  useEffect(() => {
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  });
  const move = (dir: number) => {
    const idx = items.findIndex((t) => t.value === value);
    const next = items[(idx + dir + items.length) % items.length];
    if (next) {
      onChange(next.value);
      document.getElementById(`${idPrefix}-tab-${next.value}`)?.focus();
    }
  };
  return (
    <div class={styles.tabsWrap}>
      <div
        class={styles.tabs}
        role="tablist"
        aria-label={label}
        ref={listRef}
        onScroll={updateFade}
      >
        {items.map((t) => {
          const selected = t.value === value;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              id={`${idPrefix}-tab-${t.value}`}
              aria-controls={`${idPrefix}-panel-${t.value}`}
              aria-selected={selected ? 'true' : 'false'}
              tabIndex={selected ? 0 : -1}
              class={cx(styles.tab, selected && styles.selected)}
              onClick={() => onChange(t.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') move(1);
                else if (e.key === 'ArrowLeft') move(-1);
              }}
            >
              <span>{t.label}</span>
              {t.count ? <span class={styles.count}>{t.count}</span> : null}
            </button>
          );
        })}
      </div>
      {fade ? <span class={styles.fade} aria-hidden="true" /> : null}
    </div>
  );
}
